import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SupabaseService } from 'src/app/core/supabase.service';
import { AuthService } from '../auth/auth.service';
import { Database } from 'src/app/core/database.types';

type ProfileRow = Database['public']['Tables']['profiles']['Row'];
type RankRow = Database['public']['Tables']['ranks']['Row'];
type BadgeRow = Database['public']['Tables']['badges']['Row'];
type UserBadgeRow = Database['public']['Tables']['user_badges']['Row'];
export type ProfileStats = Database['public']['Functions']['get_user_stats']['Returns'][number];
type FollowStatus = Database['public']['Functions']['check_if_following']['Returns'];


export type Profile = ProfileRow & {
  // role is extra / computed
  role?: 'user' | 'admin';
  ranks?: RankRow | null; // if you also use ranks here
};
export type Badge = BadgeRow;

@Injectable({ providedIn: 'root' })
export class ProfileService {
  // --- Signals ---
  readonly profile = signal<Profile | null>(null);
  // readonly loading = signal(false); // We are replacing this
  readonly state = signal<'pending' | 'loading' | 'loaded'>('pending'); // <-- NEW State Signal
  readonly hasProfile = computed(() => !!this.profile());
  private supabase = inject(SupabaseService);
  private auth = inject(AuthService);
  private currentProfileId: string | null = null;
  private snackBar = inject(MatSnackBar);

  // Follow-state cache and guards for optimistic updates
  private readonly followStatuses = signal<Record<string, boolean>>({});
  private readonly followInFlight = signal<Record<string, boolean>>({});
  private readonly lastFollowToggle = new Map<string, number>();
  private readonly followDebounceMs = 600;

  constructor() {
    // Keep profile data in sync with auth session
    effect(() => {
      const session = this.auth.session();
      if (session?.user?.id) {
        this.loadProfile(session.user.id);
      } else {
        this.profile.set(null);
        this.state.set('loaded'); // Loaded, but with no user
        this.currentProfileId = null;
      }
    });
  }

  async loadProfile(userId: string): Promise<Profile | null> {
    if (this.currentProfileId === userId && this.state() === 'loaded') {
      return this.profile();
    }

    if (this.state() === 'loading' && this.currentProfileId === userId) {
      return this.profile();
    }

    this.state.set('loading'); // <-- Use new state
    this.currentProfileId = userId;
    try {
      const { data, error } = await this.supabase.supabase
        .from('profiles')
        .select('*, ranks(*)')
        .eq('id', userId)
        .single<Profile>();

      if (error) {
        console.error('Error fetching profile:', error);
        this.profile.set(null);
      } else {
        this.profile.set(data);
      }
    } finally {
      this.state.set('loaded'); // <-- Use new state
    }
    return this.profile();
  }

  async getProfileById(id: string): Promise<Profile | null> {
    const { data, error } = await this.supabase.supabase
      .from('profiles')
      .select('*, ranks(*)')
      .eq('id', id)
      .single<Profile>();

    if (error) {
      console.error('Error fetching profile by id:', error);
      return null;
    }
    return data;
  }

  async getProfileByUsername(username: string): Promise<Profile | null> {
    const { data, error } = await this.supabase.supabase
      .from('profiles')
      .select('*, ranks(*)')
      .eq('username', username)
      .single<Profile>();

    if (error) {
      console.error('Error fetching profile by username:', error);
      return null;
    }
    return data;
  }

  async updateProfile(userId: string, profileData: { username: string; bio?: string; }) {
    const { data, error } = await this.supabase.supabase
      .from('profiles')
      .update(profileData)
      .eq('id', userId)
      .select();

    if (error) {
      console.error('Error updating profile', error);
      throw error;
    }

    // Refresh the cached profile
    await this.loadProfile(userId);
    return data;
  }

  async getProfileStats(id: string): Promise<ProfileStats | null> {
    const { data, error } = await this.supabase.supabase
      .rpc('get_user_stats', { user_id_param: id })
      .single<ProfileStats>();

    if (error) {
      console.error('Error fetching profile stats:', error);
      return null;
    }
    return data;
  }

  async getUserBadges(userId: string): Promise<Badge[]> {
    type UserBadgeWithBadge = UserBadgeRow & { badges: Badge | null };

    const { data, error } = await this.supabase.supabase
      .from('user_badges')
      .select('badges(*)')
      .eq('user_id', userId);

    if (error) {
      console.error('Error fetching user badges:', error);
      return [];
    }

    if (!data) {
      return [];
    }

    return data
      .map((item) => item.badges)
      .filter((badge): badge is Badge => badge !== null);
  }

  async uploadAvatar(userId: string, file: File): Promise<string | null> {
    try {
      const filePath = `${userId}/avatar`;
      const { error: uploadError } = await this.supabase.supabase.storage
        .from('avatars')
        .upload(filePath, file, { upsert: true });

      if (uploadError) {
        throw uploadError;
      }

      const { data: urlData } = this.supabase.supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);

      const publicUrl = urlData.publicUrl;

      const { error: updateError } = await this.supabase.supabase
        .from('profiles')
        .update({ avatar_url: publicUrl })
        .eq('id', userId);

      if (updateError) {
        throw updateError;
      }

      await this.loadProfile(userId);
      return publicUrl;
    } catch (error) {
      console.error('Error uploading avatar:', error);
      return null;
    }
  }

  // --- "FOLLOW" METHODS ---
  async checkIfFollowing(profileId: string): Promise<boolean> {
    const { data, error } = await this.supabase.supabase.rpc('check_if_following', {
      following_id_param: profileId
    });
    if (error) {
      console.error('Error checking follow status:', error);
      return false;
    }
    const isFollowing = (data ?? false) as FollowStatus;
    this.setFollowStatus(profileId, isFollowing);
    return isFollowing;
  }

  async followUser(profileIdToFollow: string) {
    const loggedInUserId = this.auth.session()?.user.id;
    if (!loggedInUserId) {
      throw new Error('User must be logged in to follow.');
    }

    if (!this.beginFollowOperation(profileIdToFollow)) {
      return;
    }

    const previousStatus = this.getFollowStatus(profileIdToFollow);
    this.setFollowStatus(profileIdToFollow, true);

    try {
      const { error } = await this.supabase.supabase
        .from('user_follows')
        .insert({
          follower_id: loggedInUserId,
          following_id: profileIdToFollow
        });

      if (error) {
        this.setFollowStatus(profileIdToFollow, previousStatus);
        this.snackBar.open('Failed to follow user. Please try again.', 'Dismiss', { duration: 4000 });
        throw error;
      }

      this.snackBar.open('You are now following this user.', 'Close', { duration: 3000 });
    } finally {
      this.endFollowOperation(profileIdToFollow);
    }
  }

  async unfollowUser(profileIdToUnfollow: string) {
    const loggedInUserId = this.auth.session()?.user.id;
    if (!loggedInUserId) {
      throw new Error('User must be logged in to unfollow.');
    }

    if (!this.beginFollowOperation(profileIdToUnfollow)) {
      return;
    }

    const previousStatus = this.getFollowStatus(profileIdToUnfollow);
    this.setFollowStatus(profileIdToUnfollow, false);

    try {
      const { error } = await this.supabase.supabase
        .from('user_follows')
        .delete()
        .eq('follower_id', loggedInUserId)
        .eq('following_id', profileIdToUnfollow);

      if (error) {
        this.setFollowStatus(profileIdToUnfollow, previousStatus);
        this.snackBar.open('Failed to unfollow user. Please try again.', 'Dismiss', { duration: 4000 });
        throw error;
      }

      this.snackBar.open('You have unfollowed this user.', 'Close', { duration: 3000 });
    } finally {
      this.endFollowOperation(profileIdToUnfollow);
    }
  }

  setFollowStatus(profileId: string, isFollowing: boolean) {
    this.followStatuses.update((current) => ({ ...current, [profileId]: isFollowing }));
  }

  getFollowStatus(profileId: string): boolean {
    return this.followStatuses()[profileId] ?? false;
  }

  isFollowOperationLocked(profileId: string): boolean {
    const isInFlight = this.followInFlight()[profileId];
    const lastToggle = this.lastFollowToggle.get(profileId) ?? 0;
    return !!isInFlight || Date.now() - lastToggle < this.followDebounceMs;
  }

  private beginFollowOperation(profileId: string): boolean {
    if (this.isFollowOperationLocked(profileId)) {
      return false;
    }

    this.lastFollowToggle.set(profileId, Date.now());
    this.followInFlight.update((current) => ({ ...current, [profileId]: true }));
    return true;
  }

  private endFollowOperation(profileId: string) {
    this.followInFlight.update((current) => ({ ...current, [profileId]: false }));
  }
}

